<template>
  <div
    class="sf-banner"
    :class="outerContainer"
    v-bind="$attrs"
    @click.stop.prevent="handleClick"
  >
    <span
      v-if="showIcon"
      class="sf-banner-icon icon tw-leading-[18px]"
      :class="effectiveIconClass"
    />
    <span class="sf-banner-title tw-leading-[18px]">
      {{ title }}
      <slot name="title" />
    </span>
    <div v-if="hasActions" class="sf-banner-right">
      <div class="sf-banner-actions">
        <slot name="actions" />
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import type { FlexAlignType } from '@app/shared/constants'
import { bannerVariants, BannerVariant } from '@app/shared/types/banner'

import { computed, useSlots } from 'vue'

import { icons, BannerIconType } from './constants'
import { FlexAlign } from '@app/shared/constants'

// eslint-disable-next-line no-undef
defineOptions({
  name: 'SfBannerInline',
})

const slots = useSlots()

interface Props {
  align?: Extract<FlexAlignType, 'start' | 'center'>
  iconClass?: BannerIconType
  title?: string
  variant: BannerVariant
  showIcon?: boolean
  hoverable?: boolean
}

const emit = defineEmits(['click'])

const props = withDefaults(defineProps<Props>(), {
  align: FlexAlign.CENTER,
  hasLongText: false,
  iconClass: '',
  isFullWidth: false,
  title: '',
  showIcon: true,
  variant: bannerVariants.info,
})

const effectiveIconClass = computed(() => {
  if (props.iconClass) {
    return props.iconClass
  }

  return icons[props.variant]
})

const hasActions = computed(() => {
  return !!slots.actions
})

const outerContainer = computed(() => {
  const effectiveClass = `sf-banner-inline-${props.variant}`
  return {
    [effectiveClass]: true,
    [`${effectiveClass}--hoverable`]: props.hoverable,
    'tw-items-center': props.align === FlexAlign.CENTER,
    'tw-items-start': props.align === FlexAlign.START,
  }
})

function handleClick(e: Event): void {
  emit('click', e)
}
</script>
